/**
 * Zones, derived in the browser from the STORED temperature values.
 *
 * Joel's ruling (2026-09-24): Zones is derived data. The cloud stops storing
 * ZONESTORAGE, and the browser derives it the way it already derives peak
 * temperature and cure time. The acceptance bar is BIT-IDENTITY with the
 * desktop's `Zones.stor`, sample for sample, so this is a transcription of the
 * desktop rule, not an approximation of it.
 *
 * THE RULE, read from viscom_liner `Source/ViscomLiner/Services/DataService.cs`
 * (@ 1f2359e), not from a summary of it:
 *
 *   Heat-input counters (:773-787). Per stage, per sample: +1 on every tick, tick 0
 *   included, where `TemperatureFromShort(raw) >= stage.Temperature`. A TICK COUNT
 *   held in a ushort, not elapsed time. That is why CuringAccumulators, which sums
 *   real seconds and credits nothing on the first tick, cannot be reused here.
 *
 *   State (:835-858). ASSIGNED in sequence, each test overwriting the last:
 *     Cool;
 *     Heating         if T >= CT / 2f;
 *     HeatInputCured  if ANY stage has counter * MI > Minutes * 60 (strict);
 *     Cured           if T >= CT;
 *     Overheat        if T >= OT.
 *   So a sample at or above CT is Cured (2) even when a counter already qualifies
 *   it for HeatInputCured (3). It is not a max within the tick.
 *
 *   Latch (:860). stored = Math.Max(previous stored, state). Monotone, never down.
 *
 *   Arithmetic is FLOAT32 throughout: TemperatureFromShort is `v / 10f - 273.15f`
 *   (osscad MeasurementTrace.cs:325-327), and every comparison is float32 against
 *   float32 thresholds. Doing it in float64 moves samples that sit on a threshold.
 *
 * STORAGE: TRANSITION TICKS, NOT ROWS. Because the latch never goes down, a
 * sample's whole zone history is the first tick at which it reached each state.
 * The zone at tick t is the highest state whose first tick is <= t. The cost is
 * O(width), whatever the cure length. Nothing here depends on the telemetry ring
 * buffer, so eviction cannot make it wrong, and scrubbing is exact everywhere.
 *
 * REFUSES, NEVER APPROXIMATES. A row of the wrong width, a non-integer or
 * out-of-range raw value, or a tick that does not strictly follow the last one
 * throws ZoneFoldRefused and poisons the fold. A zone computed from anything
 * else would be a plausible wrong answer.
 */

export const enum ZoneState {
  Cool = 0,
  Heating = 1,
  Cured = 2,
  HeatInputCured = 3,
  Overheat = 4,
}

/** Highest ZoneState value; transition ticks are kept for 1..ZONE_STATE_MAX. */
const ZONE_STATE_MAX = 4;

export interface ZoneHeatInput {
  /** Celsius, as stored. Parsed as float32, exactly as the desktop does. */
  temperatureC: number;
  minutes: number;
}

export interface ZoneParams {
  /** Celsius, as stored. The desktop holds CT and OT as ints. */
  curingTemperatureC: number;
  overheatTemperatureC: number;
  /** Seconds. */
  measurementIntervalS: number;
  heatInputs: readonly ZoneHeatInput[];
}

export class ZoneFoldRefused extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ZoneFoldRefused';
  }
}

const F_273_15 = Math.fround(273.15);

/** MeasurementTrace.TemperatureFromShort, in float32: `v / 10f - 273.15f`. */
export function temperatureFromShortF32(raw: number): number {
  return Math.fround(Math.fround(raw / 10) - F_273_15);
}

export class ZoneFold {
  readonly width: number;

  private readonly heatingF: number;
  private readonly curingF: number;
  private readonly overheatF: number;
  private readonly stageTempsF: number[];
  /** Minutes * 60, compared against counter * MI (both integers, as on the desktop). */
  private readonly stageLimits: number[];
  private readonly mi: number;

  private readonly latch: Uint8Array;
  private readonly counters: Uint16Array[];
  /** firstTick[s][x]: first tick at which sample x latched to state s, or -1. */
  private readonly firstTick: Int32Array[];
  private readonly epochs: number[] = [];

  private refusal: string | null = null;

  constructor(width: number, params: ZoneParams) {
    if (!Number.isInteger(width) || width <= 0) {
      throw new ZoneFoldRefused(`Zone fold needs a positive integer width, got ${width}.`);
    }
    const ct = params.curingTemperatureC;
    const ot = params.overheatTemperatureC;
    const mi = params.measurementIntervalS;
    if (!Number.isFinite(ct) || !Number.isFinite(ot) || !Number.isInteger(mi) || mi <= 0) {
      throw new ZoneFoldRefused(
        `Zone fold needs finite CT/OT and a positive integer MI (got CT=${ct}, OT=${ot}, MI=${mi}).`
      );
    }
    this.width = width;
    // `this.Project.CuringTemperature / 2f`: the int CT promoted to float, halved in float.
    this.curingF = Math.fround(ct);
    this.heatingF = Math.fround(this.curingF / 2);
    this.overheatF = Math.fround(ot);
    this.mi = mi;
    this.stageTempsF = params.heatInputs.map((h) => Math.fround(h.temperatureC));
    this.stageLimits = params.heatInputs.map((h) => h.minutes * 60);

    this.latch = new Uint8Array(width);
    this.counters = params.heatInputs.map(() => new Uint16Array(width));
    this.firstTick = [];
    for (let s = 0; s <= ZONE_STATE_MAX; s++) {
      this.firstTick.push(new Int32Array(width).fill(-1));
    }
  }

  /** Ticks folded so far. */
  get tickCount(): number {
    return this.epochs.length;
  }

  /** Why the fold stopped, or null while it is still exact. */
  get refused(): string | null {
    return this.refusal;
  }

  /** The epoch (ms) of each folded tick, in fold order. */
  epochAt(tick: number): number | undefined {
    return this.epochs[tick];
  }

  /** The fold tick whose epoch is exactly `epochMs`, or -1. */
  tickForEpoch(epochMs: number): number {
    let lo = 0;
    let hi = this.epochs.length - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const e = this.epochs[mid];
      if (e === epochMs) return mid;
      if (e < epochMs) lo = mid + 1;
      else hi = mid - 1;
    }
    return -1;
  }

  /**
   * Fold one tick of STORED values (ushorts), in tick order.
   *
   * @param raw      exactly `width` integers in [0, 65535]
   * @param epochMs  the tick's timestamp; must strictly exceed the previous one
   */
  foldRaw(raw: ArrayLike<number>, epochMs: number): void {
    if (this.refusal) {
      throw new ZoneFoldRefused(this.refusal);
    }
    if (!raw || raw.length !== this.width) {
      this.refuse(`Row width ${raw?.length ?? 'none'} does not match the fold width ${this.width}.`);
    }
    const prev = this.epochs.length > 0 ? this.epochs[this.epochs.length - 1] : undefined;
    if (!Number.isFinite(epochMs) || (prev !== undefined && !(epochMs > prev))) {
      this.refuse(
        prev !== undefined && epochMs === prev
          ? `Two ticks share the timestamp ${epochMs}. The cloud keys telemetry on the tick timestamp, so this should not happen.`
          : `Tick ${epochMs} does not follow ${prev}; ticks must arrive strictly ascending.`
      );
    }
    for (let x = 0; x < this.width; x++) {
      const v = raw[x];
      if (!Number.isInteger(v) || v < 0 || v > 0xffff) {
        this.refuse(`Sample ${x} holds ${v}, which is not a stored ushort. Was the row decoded or converted?`);
      }
    }

    const tick = this.epochs.length;
    const stages = this.stageTempsF.length;

    for (let x = 0; x < this.width; x++) {
      const t = temperatureFromShortF32(raw[x]);

      // Counters first: the desktop's zone pass reads the counter row for the SAME
      // tick, which already includes this tick.
      for (let j = 0; j < stages; j++) {
        if (t >= this.stageTempsF[j]) {
          this.counters[j][x] = (this.counters[j][x] + 1) & 0xffff;
        }
      }

      let state: number = ZoneState.Cool;
      if (t >= this.heatingF) state = ZoneState.Heating;
      for (let j = 0; j < stages; j++) {
        if (this.counters[j][x] * this.mi > this.stageLimits[j]) {
          state = ZoneState.HeatInputCured;
          break;
        }
      }
      if (t >= this.curingF) state = ZoneState.Cured;
      if (t >= this.overheatF) state = ZoneState.Overheat;

      if (state > this.latch[x]) {
        this.latch[x] = state;
        this.firstTick[state][x] = tick;
      }
    }

    this.epochs.push(epochMs);
  }

  /** The latched zone of every sample at `tick` (0-based fold tick). */
  zoneRowAt(tick: number): Uint8Array {
    const row = new Uint8Array(this.width);
    if (tick < 0) return row;
    for (let x = 0; x < this.width; x++) {
      for (let s = ZONE_STATE_MAX; s >= 1; s--) {
        const first = this.firstTick[s][x];
        if (first >= 0 && first <= tick) {
          row[x] = s;
          break;
        }
      }
    }
    return row;
  }

  /** The latched zones after the last folded tick. A copy. */
  latestRow(): Uint8Array {
    return this.latch.slice();
  }

  /** Stage `j`'s counter row after the last folded tick. A copy, for the oracle. */
  counterRow(j: number): Uint16Array {
    return this.counters[j].slice();
  }

  private refuse(reason: string): never {
    this.refusal = reason;
    throw new ZoneFoldRefused(reason);
  }
}
